import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Row, Col, FormGroup, Label, Input } from "reactstrap";
import { useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../hooks/useTheme";
import { formatDateTime } from "../../../utils/dateUtils";
import { TextInput, NumberInput, CurrencyInput, DateTimeInput, PercentageInput } from "../../../components/DataInputs";
import _ from 'lodash';
import DialogBox from "../../../components/DialogBox";
import CustomSelect from "../../../components/CustomSelect";
import { FormattedNumberField } from "../../../components/DataInputs";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { fetchSalesforceCodes } from "../../salesforceCodes/data/salesforceCodeService";                                                                                        

const ItemVersionModal = ({ isOpen, toggle, version, isEditable = false, onSave }) => {
    const [localVersion, setLocalVersion] = useState(version);
    const [originalLocalVersion, setOriginalLocalVersion] = useState(version);
    const [isDialogBoxOpen, setIsDialogBoxOpen] = useState(false);
    const theme = useTheme();

    const [currentActiveTab, setCurrentActiveTab] = useState("Upfront");

    const { 
        data: salesForceCodesData,
        isLoading: isLoadingSalesForceCodes,
        error: salesForceCodesError,
    } = useQuery({
        queryKey: ["salesforceCodes"],
        queryFn: fetchSalesforceCodes,
        staleTime: 60000
    });

    const sfCodes = salesForceCodesData?.filter(x => !x.disabled) ?? [];

    const customStyles = {
        option: (styles, { isSelected }) => ({
            ...styles,
            fontFamily: "Hanken Grotesk, Sans-serif",
            fontWeight: isSelected ? "bold" : "normal",
            color: isSelected ? "#fff" : "",
            lineHeight: "0.8em",
        }),
    };

    useEffect(() => {
        setLocalVersion(version);
        setOriginalLocalVersion(version);
    }, [version]);

    useEffect(() => {
        setCurrentActiveTab("Upfront");
    }, [isOpen]);

    const handleClose = () => {
        if (!_.isEqual(localVersion, originalLocalVersion)) {
            setIsDialogBoxOpen(true);
        } else {
            toggle();
        }
    };

    const saveChanges = () => {
        if (_.isEqual(localVersion, originalLocalVersion)) {
            toggle();
            return;
        }

        if (!localVersion.isUpfront && !localVersion.isRecurring) {
            toast.warning("Please select one of the billing options (upfront or recurring).");
            return
        }

        const gracePeriodInMinutes = 5
        if (new Date(localVersion.activeStartDate) < new Date(Date.now() - gracePeriodInMinutes * 60 * 1000)) {
            toast.warning(`The selected start date is in the past. Please choose a future date.`);
            return
        }

        if (localVersion.isUpfront) {
            if (localVersion.upfrontCost == null || localVersion.upfrontCost === "") {
                toast.warning("Please enter a value for Upfront Cost.");
                return
            }
            if (localVersion.upfrontPrice == null || localVersion.upfrontPrice === "") {
                toast.warning("Please enter a value for Upfront Price.");
                return
            }

            if (!localVersion.upfrontSalesforceCode) {
                toast.warning("Please select a value for Upfront Salesforce Code.");
                return
            }

            if (!upfrontSalesforceCodes.find(x => x.name === localVersion.upfrontSalesforceCode)) {
                toast.warning("Please select a valid Upfront Salesforce Code.");
                return
            }
        }

        if (localVersion.isRecurring) {
            if (localVersion.recurringCost == null || localVersion.recurringCost === "") {
                toast.warning("Please enter a value for Recurring Cost.");
                return
            }

            if (localVersion.recurringPrice == null || localVersion.recurringPrice === "") {
                toast.warning("Please enter a value for Recurring Price.");
                return
            }

            if (!localVersion.recurringSalesforceCode) {
                toast.warning("Please select a value for Recurring Salesforce Code.");
                return
            }

            if (!recurringSalesforceCodes.find(x => x.name === localVersion.recurringSalesforceCode)) {
                toast.warning("Please select a valid Recurring Salesforce Code.");
                return
            }        
        }

        const versionToSave = {...localVersion}
        const hasLoadedSalesforceCodes = Array.isArray(sfCodes) && sfCodes.length > 0;
        const supportsPaidUpfrontRecognition = hasLoadedSalesforceCodes &&
            sfCodes.some(code =>
                code.name === versionToSave.upfrontSalesforceCode &&
                code.type === "Recurring" &&
                code.canBePaidUpfront
            );

        if (!versionToSave.isUpfront) {
            versionToSave.upfrontRecognitionMonths = null;
        } else if (hasLoadedSalesforceCodes && !supportsPaidUpfrontRecognition) {
            versionToSave.upfrontRecognitionMonths = null;
        }

        setLocalVersion(versionToSave);
        setOriginalLocalVersion(versionToSave);
        onSave(versionToSave);
    };

    const limitDecimals = (value, maxDecimals) => {
        const valueToString = value.toString();
        return parseFloat(valueToString.slice(0, valueToString.indexOf(".") + (maxDecimals + 1)));
    };

    const updateField = (field, value, maxDecimals = null) => {
        if (maxDecimals !== null) {
            value = limitDecimals(value, maxDecimals);
        }
        setLocalVersion(prev => ({ ...prev, [field]: value }));
    };

    const handleUpfrontSalesforceCodeChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : null;
        updateField("upfrontSalesforceCode", value);
    };

    const handleRecurringSalesforceCodeChange = (selectedOption) => {
        const value = selectedOption ? selectedOption.value : null;
        updateField("recurringSalesforceCode", value);
    }

    const updateCheckbox = (field) => {
        setLocalVersion(prev => {
            const newValue = !prev[field];
            let updated = { ...prev, [field]: newValue };

            if (field === "isUpfront" && !newValue) {
                updated = {
                    ...updated,
                    quoteUpfrontDescription: null,
                    upfrontCost: null,
                    upfrontPrice: null,
                    upfrontCostDefaultDiscount: null,
                    upfrontCostDiscountThreshold: null,
                    upfrontPriceDefaultDiscount: null,
                    upfrontPriceDiscountThreshold: null
                };
            } else if (field === "isRecurring" && !newValue) {
                updated = {
                    ...updated,
                    quoteRecurringDescription: null,
                    recurringCost: null,
                    recurringPrice: null,
                    recurringStartMonth: null,
                    recurringIntervalMonths: null,
                    recurringNumPayments: null,
                    recurringCostDefaultDiscount: null,
                    recurringCostDiscountThreshold: null,
                    recurringPriceDefaultDiscount: null,
                    recurringPriceDiscountThreshold: null
                };
            }

            return updated;
     });
    };

    const upfrontSalesforceCodes = [
        ...(localVersion?.isUpfront && localVersion?.upfrontSalesforceCode && !sfCodes.some(x => (x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront)) && x.name === localVersion.upfrontSalesforceCode) ? [{ name: localVersion.upfrontSalesforceCode, label: localVersion.upfrontSalesforceCode + " (INVALID)" }] : []),
        ...(sfCodes.filter(x => x.type === "Upfront" || (x.type === "Recurring" && x.canBePaidUpfront)) ?? [])
    ]
    const recurringSalesforceCodes = [
        ...(localVersion?.isRecurring && localVersion?.recurringSalesforceCode && !sfCodes.filter(x => x.type === "Recurring").find(x => x.name === localVersion.recurringSalesforceCode) ? [{ name: localVersion.recurringSalesforceCode, label: localVersion.recurringSalesforceCode + " (INVALID)" }] : []),
        ...(sfCodes.filter(x => x.type === "Recurring") ?? [])
    ];

    const itemErrors = useMemo(() => {
        const errors = [];

        // Not loaded yet
        if (!localVersion) return errors;

        if (localVersion.isUpfront) {
            if (localVersion.upfrontCost == null || localVersion.upfrontCost === "") { errors.push({ field: "upfrontCost", error: "Upfront Cost value is required" }) }
            if (localVersion.upfrontPrice == null || localVersion.upfrontPrice === "") { errors.push({ field: "upfrontPrice", error: "Upfront Price value is required" }) }
            if (!localVersion.upfrontSalesforceCode) { errors.push({ field: "upfrontSalesforceCode", error: "Upfront Salesforce Code value is required" }) }
            if (upfrontSalesforceCodes.find(code => code.name === localVersion.upfrontSalesforceCode && code.label?.includes("(INVALID)"))) { errors.push({ field: "upfrontSalesforceCode", error: "Selected Salesforce Code is invalid" }) }

            if (sfCodes.some(x => x.name === localVersion.upfrontSalesforceCode && x.type === "Recurring" && x.canBePaidUpfront)) {
                if (localVersion.upfrontRecognitionMonths > 999) {
                    errors.push({ field: "upfrontRecognitionMonths", error: "Recognition Period (Months) max value is 999" })
                }
                // Note that we don't check here for minimum values, or that it's even supplied, as for catalogue items this is
                // not a required field and can be set to null. For quote items, the validation is handled separately in the
                // quote item modal and tables, where we can enforce that it's supplied and a positive integer.
            }
        }

        if (localVersion.isRecurring) {
            if (localVersion.recurringCost == null || localVersion.recurringCost === "") { errors.push({ field: "recurringCost", error: "Recurring Cost value is required" }) }
            if (localVersion.recurringPrice == null || localVersion.recurringPrice === "") { errors.push({ field: "recurringPrice", error: "Recurring Price value is required" }) }
            if (!localVersion.recurringSalesforceCode) { errors.push({ field: "recurringSalesforceCode", error: "Recurring Salesforce Code value is required" }) }
            if (recurringSalesforceCodes.find(code => code.name === localVersion.recurringSalesforceCode && code.label?.includes("(INVALID)"))) { errors.push({ field: "recurringSalesforceCode", error: "Selected Salesforce Code is invalid" }) }
        }
        return errors;
    }, [localVersion]);

    if (!localVersion) return null;

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="xl" centered backdrop="static">
            <ModalHeader toggle={toggle}>
                {localVersion.state !== 'future' ? <i className="bi bi-lock"></i> : <i className="bi bi-unlock"></i>} {formatDateTime(localVersion.activeStartDate) || "New Version"}
            </ModalHeader>
            <ModalBody>
                <>
                    <FormGroup row>
                        <Label sm={4}>Active Start Date</Label>
                        <Col sm={8}>
                            <DateTimeInput value={localVersion.activeStartDate} isEditable={isEditable} onChange={(e) => updateField("activeStartDate", e.target.value)} />
                        </Col>
                    </FormGroup>
                    <hr />
                    {/* Tabs */}
                    <Row className="modal-menu">
                        <Col style={{ display: 'inline-flex' }}>
                            {["Upfront", "Recurring"].map((tab) => (
                                <h6
                                    key={tab}
                                    className={currentActiveTab === tab ? "custom-nav-selected custom-nav m-3" : "custom-nav m-3"}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => setCurrentActiveTab(tab)}
                                >
                                    {tab}
                                </h6>
                            ))}
                        </Col>
                    </Row>

                    {/* Upfront Tab */}
                    {currentActiveTab === "Upfront" && (
                        <>
                            <Row>
                                <Col>
                                    <FormGroup row>
                                        <Label sm={4}>Is Upfront?</Label>
                                        <Col sm={8}>
                                            <Input
                                                type="checkbox"
                                                disabled={!isEditable}
                                                checked={localVersion.isUpfront}
                                                onChange={() => updateCheckbox("isUpfront")}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Name on Quote</Label>
                                        <Col sm={8}>
                                            <TextInput value={localVersion.quoteUpfrontDescription} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("quoteUpfrontDescription", e.target.value)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Salesforce Code</Label>
                                        <Col sm={8}>
                                            <CustomSelect
                                                name="upfrontSalesforceCode"
                                                isSearchable
                                                isDisabled={!isEditable || !localVersion.isUpfront}
                                                options={upfrontSalesforceCodes.map((item) => ({
                                                    value: item.name,
                                                    label: item.type === "Recurring" ? item.name + " 🔁" : item.label ?? item.name,
                                                    //label: item.replace('COE_', '').replace('__c', '')
                                                }))}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    ...customStyles,
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    menu: base => ({ ...base, zIndex: 9999 })
                                                }}
                                                classNamePrefix="data-select"
                                                onChange={handleUpfrontSalesforceCodeChange}
                                                value={localVersion.upfrontSalesforceCode?.trim()}
                                                invalid={itemErrors.find(x => x.field === "upfrontSalesforceCode")}
                                            />                                       
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Cost</Label>
                                        <Col sm={8}>
                                            <CurrencyInput name="upfrontCost" value={localVersion.upfrontCost} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("upfrontCost", parseFloat(e.target.value) || 0, 12)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Price</Label>
                                        <Col sm={8}>
                                            <CurrencyInput name="upfrontPrice" value={localVersion.upfrontPrice} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("upfrontPrice", parseFloat(e.target.value) || 0, 12)} />
                                        </Col>
                                    </FormGroup>

                                    {   upfrontSalesforceCodes.some(code => code.name === localVersion.upfrontSalesforceCode && code.type === "Recurring" && code.canBePaidUpfront) &&
                                        <FormGroup row style={localVersion.isUpfront ? {} : { visibility: "hidden" }}>
                                            <Label sm={4}>Recognition Period (Months)</Label>
                                            <Col>
                                                <FormattedNumberField
                                                    id="upfrontRecognitionMonths"
                                                    name="upfrontRecognitionMonths"
                                                    numDecimalPlaces={0}
                                                    value={localVersion.upfrontRecognitionMonths}
                                                    isEditable={isEditable && localVersion.isUpfront}
                                                    onChange={(e) => {
                                                        const num = Number(e.target.value);
                                                        updateField(
                                                            "upfrontRecognitionMonths",
                                                            Number.isNaN(num) ? null : Math.trunc(num),
                                                            null
                                                        );
                                                    }}
                                                    invalid={itemErrors.find(x => x.field === "upfrontRecognitionMonths")}
                                                    style={{ width: "75px" }}
                                                />
                                            </Col>
                                            <Row>
                                                <Col sm={4} className="text-muted" style={{ fontSize: "0.875rem" }}>
                                                    
                                                </Col>
                                                <Col>
                                                    {itemErrors.find(x => x.field === "upfrontRecognitionMonths") && (
                                                        <div style={{ color: '#EA4D5E', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                                            {itemErrors.find(x => x.field === "upfrontRecognitionMonths")?.error}
                                                        </div>
                                                    )}

                                                    <div className="text-muted m-0 p-2" style={{ fontSize: "0.875rem" }}>
                                                        <i className="bi bi-info-circle-fill me-2"></i>Number of months over which upfront revenue is recognised in Salesforce
                                                    </div>

                                                </Col>
                                            </Row>

                                        </FormGroup>
                                    }

                                </Col>

                                <Col>

                                    {/* <h6 className="text-primary">Discounts</h6> */}

                                    <FormGroup row>
                                        <Label sm={4}>Cost Default Discount</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="upfrontCostDefaultDiscount" value={localVersion.upfrontCostDefaultDiscount} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("upfrontCostDefaultDiscount", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Cost Discount Threshold</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="upfrontCostDiscountThreshold" value={localVersion.upfrontCostDiscountThreshold} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("upfrontCostDiscountThreshold", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Price Default Discount</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="upfrontPriceDefaultDiscount" value={localVersion.upfrontPriceDefaultDiscount} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("upfrontPriceDefaultDiscount", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Price Discount Threshold</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="upfrontPriceDiscountThreshold" value={localVersion.upfrontPriceDiscountThreshold} isEditable={isEditable && localVersion.isUpfront} onChange={(e) => updateField("upfrontPriceDiscountThreshold", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>
                                </Col>
                            </Row>
                        </>
                    )}

                    {/* Recurring Tab */}
                    {currentActiveTab === "Recurring" && (
                        <>
                            <Row>
                                <Col>
                                    <FormGroup row>
                                        <Label sm={4}>Is Recurring?</Label>
                                        <Col sm={8}>
                                            <Input
                                                type="checkbox"
                                                disabled={!isEditable}
                                                checked={localVersion.isRecurring}
                                                onChange={() => updateCheckbox("isRecurring")}
                                            />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Name on Quote</Label>
                                        <Col sm={8}>
                                            <TextInput value={localVersion.quoteRecurringDescription} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("quoteRecurringDescription", e.target.value)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Salesforce Code</Label>
                                        <Col sm={8}>
                                            <CustomSelect
                                                name="recurringSalesforceCode"
                                                isSearchable
                                                isDisabled={!isEditable || !localVersion.isRecurring}
                                                options={recurringSalesforceCodes.map((item) => ({
                                                    value: item.name,
                                                    label: item.label ?? item.name,
                                                    //label: item.replace('COE_', '').replace('__c', '')
                                                }))}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"
                                                styles={{
                                                    ...customStyles,
                                                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                                                    menu: base => ({ ...base, zIndex: 9999 })
                                                }}
                                                classNamePrefix="data-select"
                                                onChange={handleRecurringSalesforceCodeChange}
                                                value={localVersion.recurringSalesforceCode?.trim()}
                                                invalid={itemErrors.find(x => x.field === "recurringSalesforceCode")}
                                            />
                                        </Col>                                    
                                    </FormGroup>                                    

                                    <FormGroup row>
                                        <Label sm={4}>Cost</Label>
                                        <Col sm={8}>
                                            <CurrencyInput name="recurringCost" value={localVersion.recurringCost} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringCost", parseFloat(e.target.value) || 0, 12)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Price</Label>
                                        <Col sm={8}>
                                            <CurrencyInput name="recurringPrice" value={localVersion.recurringPrice} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringPrice", parseFloat(e.target.value) || 0, 12)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Start Month</Label>
                                        <Col sm={8}>
                                            <NumberInput name="recurringStartMonth" value={localVersion.recurringStartMonth} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringStartMonth", parseInt(e.target.value) || 0, null)} />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label sm={4}>Interval Months</Label>
                                        <Col sm={8}>
                                            <NumberInput name="recurringIntervalMonths" value={localVersion.recurringIntervalMonths} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringIntervalMonths", parseInt(e.target.value) || 0, null)} />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup row>
                                        <Label sm={4}>Num Payments</Label>
                                        <Col>
                                            <NumberInput name="recurringNumPayments" value={localVersion.recurringNumPayments} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringNumPayments", parseInt(e.target.value) || 0, null)} />
                                        </Col>
                                    </FormGroup>

                                </Col>

                                <Col>

                                    {/* <h6 className="text-primary">Discounts</h6> */}

                                    <FormGroup row>
                                        <Label sm={4}>Cost Default Discount</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="recurringCostDefaultDiscount" value={localVersion.recurringCostDefaultDiscount} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringCostDefaultDiscount", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Cost Discount Threshold</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="recurringCostDiscountThreshold" value={localVersion.recurringCostDiscountThreshold} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringCostDiscountThreshold", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Price Default Discount</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="recurringPriceDefaultDiscount" value={localVersion.recurringPriceDefaultDiscount} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringPriceDefaultDiscount", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                    <FormGroup row>
                                        <Label sm={4}>Price Discount Threshold</Label>
                                        <Col sm={8}>
                                            <PercentageInput name="recurringPriceDiscountThreshold" value={localVersion.recurringPriceDiscountThreshold} isEditable={isEditable && localVersion.isRecurring} onChange={(e) => updateField("recurringPriceDiscountThreshold", parseFloat(e.target.value) || 0, 2)} />
                                        </Col>
                                    </FormGroup>

                                </Col>
                            </Row>
                        </>
                    )}
                </>
            </ModalBody>

            <ModalFooter>
                <Button outline onClick={handleClose} color={theme === "dark-mode" ? "dark" : "light"}>Close</Button>
                {isEditable && <Button color="primary" onClick={saveChanges}>Save</Button>}
            </ModalFooter>

            <DialogBox
                isOpen={isDialogBoxOpen}
                title="Save Changes?"
                message={
                    <div className="d-flex align-items-center">
                        <i className="bi bi-exclamation-triangle-fill text-warning fs-1 me-3"></i>
                        <span>You have unsaved changes. <br />Save changes before closing?</span>
                    </div>
                }
                toggle={() => setIsDialogBoxOpen(false)}
                buttons={[
                    {
                        label: 'Yes',
                        color: 'primary',
                        onClick: () => { saveChanges(); setIsDialogBoxOpen(false); }
                    },
                    {
                        label: 'No',
                        color: 'secondary',
                        onClick: () => { setIsDialogBoxOpen(false); toggle(); }
                    },
                    {
                        label: 'Cancel',
                        color: 'secondary',
                        onClick: () => { setIsDialogBoxOpen(false); }
                    }
                ]}
            />
        </Modal>
    );
};



export default ItemVersionModal;
